import React, { FC, KeyboardEvent, useEffect, useState } from 'react';
import { DialogComponent } from 'ReusableComponents/Modal/ModalComponent';
import { Dayjs } from 'dayjs';
import { CourierJson } from 'types/bundleTypes';
import { ButtonComponent } from 'ReusableComponents/Button/ButtonComponent';
import { IShift, ShiftExtraWork, updateExtraWork } from '../shiftApi';
import { CourierLists, CourierListSection } from './courierModalComponents';
import './CourierModals.scss';
import { BiSearch } from 'react-icons/bi';
import { DropdownComponent } from '../../../ReusableComponents/Dropdown/DropdownComponent';
import { InputComponent } from '../../../ReusableComponents/Input/InputComponent';
import { uniqBy } from 'lodash';
import clsx from 'clsx';
import Swal from 'sweetalert2';
import { ExtraWorkWithCustomName, initialExtraWorkList } from '../utils';
import { capitalize } from '../../../utils/strings';

export const CourierWorkModal: FC<{
  date: Dayjs;
  courierShiftMap: { [courierId: number]: IShift };
  couriers: CourierJson[];
  showModal: boolean;
  setShowModal: (open: boolean) => void;
}> = ({ date, courierShiftMap, couriers, showModal, setShowModal }) => {
  const [displayedCouriers, setDisplayedCouriers] = useState<CourierLists>({});
  const [selectedCouriers, setSelectedCouriers] = useState<Set<number>>(
    new Set()
  );
  const [workAssignedToCouriers, setWorkAssignedToCouriers] = useState<
    Record<string, string[]>
  >({});

  const [courierSearch, setCourierSearch] = useState<string>('');
  const [searchString, setSearchString] = useState<string>('');

  const [selectedWorkType, setSelectedWorkType] = useState<string>('');
  const [newCustomWorkAmount, setNewCustomWorkAmount] = useState<string>('');
  const [newCustomWorkName, setNewCustomWorkName] = useState<string>('');

  const [extraWorkList, setExtraWorkList] =
    useState<ExtraWorkWithCustomName[]>(initialExtraWorkList);
  const [displayedExtraWorkList, setDisplayedExtraWorkList] = useState<
    { name: string; value: string }[]
  >([]);

  // Map extra work dropdown list
  const updateDisplayedExtraWorkList = (
    newExtraWorkList: ExtraWorkWithCustomName[]
  ) => {
    const newDisplayedExtraWorkList = newExtraWorkList.map(extraWork => ({
      name: extraWork.name,
      value: extraWork.name,
    }));
    setDisplayedExtraWorkList(newDisplayedExtraWorkList);
  };

  const handleEnterPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') setCourierSearch(searchString.toLowerCase());
  };

  const applyExtraWorkChanges = () => {
    const shiftsToUpdate: ShiftExtraWork[] = [];
    Object.keys(workAssignedToCouriers).forEach(courierId => {
      const shiftId = courierShiftMap[courierId].id;
      const workTypes = workAssignedToCouriers[courierId];
      const extraWork = extraWorkList
        .filter(work => workTypes.includes(work.name))
        .map(work => work.extraWork);
      shiftsToUpdate.push({ shiftId, extraWork });
    });

    updateExtraWork(shiftsToUpdate)
      .then(() => setShowModal(false))
      .catch(error => {
        console.error(error);
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: error,
          showConfirmButton: true,
        });
      });
  };

  const setCouriersForSelectedWorkType = (workType: string) => {
    const couriers = Object.keys(workAssignedToCouriers)
      .filter(courier => workAssignedToCouriers[courier].includes(workType))
      .map(courier => +courier);
    setSelectedCouriers(new Set(couriers));
  };

  const addNewExtraWork = () => {
    // number type input ensures that the amount value will be a number
    if (newCustomWorkName.length > 0 && newCustomWorkAmount.length > 0) {
      const newDisplayedWorkName = `${capitalize(
        newCustomWorkName
      )} (${newCustomWorkAmount})`;

      setExtraWorkList(oldList => {
        if (oldList.find(work => work.name === newDisplayedWorkName)) {
          // If added work exists on the list, show already assigned couriers
          setCouriersForSelectedWorkType(newDisplayedWorkName);
          return oldList;
        }
        setSelectedCouriers(new Set());
        const newExtraWork: ExtraWorkWithCustomName = {
          name: newDisplayedWorkName,
          extraWork: {
            typeOfExtraWork: newCustomWorkName.toUpperCase(),
            payment: +newCustomWorkAmount,
          },
        };
        updateDisplayedExtraWorkList([...oldList, newExtraWork]);
        return [...oldList, newExtraWork];
      });
      setSelectedWorkType(newDisplayedWorkName);
      setNewCustomWorkAmount('');
      setNewCustomWorkName('');
    }
  };

  // Filter couriers by search criteria
  useEffect(() => {
    const filteredCouriers = couriers.filter(courier =>
      courier.fullName.toLowerCase().includes(courierSearch)
    );
    setDisplayedCouriers({ '': filteredCouriers });
  }, [couriers, courierSearch]);

  // Populate extra work list based on unique courier shifts data
  useEffect(() => {
    const workAssignedToCouriers = {};
    const extraWorkFromShifts = Object.values(courierShiftMap).flatMap(
      shift => {
        workAssignedToCouriers[shift.courierId] = [];
        return shift.extraWork.map(extraWork => {
          const workName = `${capitalize(extraWork.typeOfExtraWork)} (${
            extraWork.payment
          })`;
          workAssignedToCouriers[shift.courierId].push(workName);
          return { name: workName, extraWork: extraWork };
        });
      }
    );

    setExtraWorkList(oldState => {
      const uniqueExtraWorks = uniqBy(
        [...oldState, ...extraWorkFromShifts],
        'name'
      );
      updateDisplayedExtraWorkList(uniqueExtraWorks);
      return uniqueExtraWorks;
    });
    setWorkAssignedToCouriers(workAssignedToCouriers);
  }, [courierShiftMap]);

  return (
    <DialogComponent
      className="courierPickerModal"
      open={showModal}
      setOpen={setShowModal}
    >
      <div className="courierPickerModal__hero">
        <h1>Additional work</h1>
        <p>{date.format('DD.MM.YYYY')}</p>
      </div>
      <div className="courierPickerModal__work-type">
        <div className="courierPickerModal__work-type__dropdown-container">
          <p>Select work type</p>
          <div>
            <DropdownComponent
              formClassName="courierPickerModal__work-type__dropdown"
              id="workTypeDropdown"
              labelId="workTypeDropdown"
              label="Select from list"
              value={selectedWorkType}
              itemsToSelect={displayedExtraWorkList}
              onChange={event => {
                const workType = event.target.value as string;
                setSelectedWorkType(workType);
                setCouriersForSelectedWorkType(workType);
              }}
              variant="outlined"
            />
          </div>
        </div>
        <div className="courierPickerModal__work-type__custom-work">
          <p>Add custom work</p>
          <div className="courierPickerModal__work-type__custom-work__inputs">
            <InputComponent
              margin="none"
              id="customWorkAmountInput"
              className="courierPickerModal__work-type__custom-work__inputs__amount-input"
              label="Enter amount"
              name="amountInput"
              value={newCustomWorkAmount}
              type="number"
              variant="outlined"
              onChange={e => setNewCustomWorkAmount(e.target.value)}
            />
            <InputComponent
              margin="none"
              id="customWorkNameInput"
              className="courierPickerModal__work-type__custom-work__inputs__name-input"
              label="Enter name"
              name="nameInput"
              value={newCustomWorkName}
              variant="outlined"
              onChange={e => setNewCustomWorkName(e.currentTarget.value)}
            />
            <ButtonComponent
              className={clsx(
                'courierPickerModal__work-type__custom-work__inputs__button',
                'sharedButton'
              )}
              text="Add custom work"
              customColor={{ background: '#443df6', color: 'white' }}
              onClick={() => {
                addNewExtraWork();
              }}
            />
          </div>
        </div>
      </div>
      <div className="courierPickerModal__search">
        <p>Couriers {date.format('MMM Do YYYY')}</p>
        <div className="courierPickerModal__search__container">
          <InputComponent
            margin="none"
            id="searchInput"
            className="courierPickerModal__search__container__input"
            label="Search courier"
            name="searchInput"
            value={searchString}
            variant="outlined"
            onKeyPress={handleEnterPress}
            onChange={e => setSearchString(e.currentTarget.value)}
          />
          <ButtonComponent
            className="courierPickerModal__search__container__button"
            onClick={() => setCourierSearch(searchString.toLowerCase())}
            tabIndex={0}
            customColor={{ background: '#666666', color: 'white' }}
            startIcon={<BiSearch />}
          />
        </div>
      </div>
      <CourierListSection
        selectedCouriers={selectedCouriers}
        setSelectedCouriers={setSelectedCouriers}
        courierLists={displayedCouriers}
        selectedWorkType={selectedWorkType}
        setWorkAssignedToCouriers={setWorkAssignedToCouriers}
        allowAllCouriersSelection={false}
        hideCurrentPreviousColumns
      />
      <div className="courierPickerModal__buttons">
        <ButtonComponent
          className={clsx(
            'courierPickerModal__buttons__confirm',
            'sharedButton'
          )}
          text="Apply changes"
          customColor={{ background: '#443df6', color: 'white' }}
          onClick={() => {
            applyExtraWorkChanges();
          }}
        />
        <ButtonComponent
          className={clsx(
            'courierPickerModal__buttons__cancel',
            'sharedButton'
          )}
          text="Cancel"
          onClick={() => setShowModal(false)}
        />
      </div>
    </DialogComponent>
  );
};
